/**
 * Figura sucessória efetiva a partir das seções do inventário (Herdeiros Testamentários, Legados, Doações).
 * Centraliza a definição de tipo e quota sem depender do campo "Tipo de Herdeiro" no cadastro de pessoa.
 */

import type { Inventario, TipoHerdeiro } from '../types';

export interface EffectiveFigura {
  tipo: TipoHerdeiro;
  /** Porcentagem da parte disponível (0–100). */
  quota_testamentaria?: number | string;
}

/**
 * Retorna a figura sucessória efetiva da pessoa com base nas seções do inventário.
 * Ordem de precedência: Herdeiro Testamentário (seção) > Legatário (legados) > Donatário (doações) > Herdeiro Legítimo.
 */
export function getEffectiveFiguraSucessoria(
  personId: string,
  inventario: Inventario
): EffectiveFigura {
  const idStr = String(personId);

  const ht = (inventario.herdeiros_testamentarios || []).find(
    (h) => String(h.pessoa_inventario_id) === idStr
  );
  if (ht) {
    return { tipo: 'Herdeiro Testamentário', quota_testamentaria: ht.quota_testamentaria };
  }

  const isLegatario = (inventario.legados || []).some(
    (l) => String(l.pessoa_inventario_id) === idStr
  );
  if (isLegatario) return { tipo: 'Legatário' };

  const isDonatario = (inventario.doacoes || []).some((d) =>
    (d.doacao_pessoas_inventario || []).some(
      (dp) => String(dp.pessoa_inventario_id) === idStr
    )
  );
  if (isDonatario) return { tipo: 'Donatário' };

  return { tipo: 'Herdeiro Legítimo' };
}

/** Retorna a quota testamentária (porcentagem) da pessoa se constar na seção Herdeiros Testamentários. */
export function getQuotaTestamentaria(
  personId: string,
  inventario: Inventario
): number | string | undefined {
  const ht = (inventario.herdeiros_testamentarios || []).find(
    (h) => String(h.pessoa_inventario_id) === String(personId)
  );
  return ht?.quota_testamentaria;
}
